
import { useState } from 'react';
import { useImageGeneration } from '@/hooks/runware';
import { toast } from '@/hooks/errors/use-toast';

const COVER_IMAGE_PROMPT_ID = 'cover-image-base-001';

export const useCoverImageGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { generateImage } = useImageGeneration();

  const getImageTypeFromStyle = (styleId?: string): string | undefined => {
    if (!styleId) return undefined;
    
    // Map old style IDs to new image type IDs
    const styleToTypeMap: Record<string, string> = {
      'style-photo': 'type-photo',
      'style-art': 'type-art',
      'style-anime': 'type-anime',
      'style-3d': 'type-3d'
    };
    
    return styleToTypeMap[styleId];
  };

  const generateCoverImage = async (description: string, styleId?: string): Promise<string | null> => {
    if (!description.trim()) {
      setError('Please provide a description for your cover image');
      toast({
        title: "Description required",
        description: "Please describe the cover image you want",
        variant: "warning",
      });
      return null;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const imageType = getImageTypeFromStyle(styleId);
      
      console.log('Generating cover image with params:', {
        basePromptId: COVER_IMAGE_PROMPT_ID,
        additionalText: description,
        imageType: imageType
      });
      
      // Generate the image using the consolidated hook with backend prompt building
      const generatedImages = await generateImage({
        basePromptId: COVER_IMAGE_PROMPT_ID,
        additionalText: description,
        imageType: imageType,
        width: 1024,
        height: 576, // 16:9 aspect ratio
        numberResults: 1,
        format: 'webp',
        quality: 90
      });
      
      if (!generatedImages || generatedImages.length === 0) {
        throw new Error('No images were generated');
      }
      
      const imageUrl = generatedImages[0].imageURL;
      setGeneratedImageUrl(imageUrl);
      
      toast({
        title: "Cover image generated successfully",
        variant: "success",
      });
      
      return imageUrl;
    } catch (err: any) {
      console.error('[useCoverImageGeneration] Error generating cover image:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      
      setError(errorMessage);
      
      toast({
        title: "Failed to generate cover image",
        description: errorMessage,
        variant: "error",
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  };
  
  return {
    generateCoverImage,
    isGenerating,
    generatedImageUrl,
    error,
  };
};
